/*
 * About ▸ Groups: the groups a channel belongs to, in the details column between the facts and the
 * members. Everybody in one of these groups finds the channel and may join it, which is the one
 * sentence the section leads with; the groups themselves are drawn as the person family draws a
 * group — the square, the name, the handle — so they read like the people under them.
 *
 * Only a channel has groups. A private channel is its members and nothing else, and a direct
 * conversation is its people; neither draws this section.
 *
 * *Change groups* opens the same *Choose groups* dialog the AI chats use, with the channel's owners
 * ticked and every group that may own a channel offered (never a person's own group). The dialog
 * never hands back an empty set, and `changeOwners` adds before it removes, so the channel is never
 * left without a group by this door. It does not warn that the reader is in none of the ticked
 * groups: a member reads the channel whatever its groups are. An administrator outside the channel
 * who ticks a group they are in is asked first, because that change hands them its messages — the
 * one way to read a channel without *Join as administrator*, and as visible as joining.
 *
 * Who may change them is the matrix's `chat.owner.add`/`.remove`: a member of the channel who is a
 * team member, or an administrator without being in it. A guest never may, even inside the channel:
 * a guest must not widen what reaches them. An archived channel takes no change at all.
 */

import { useMemo, useState } from "react"
import { ChooseGroupsDialog } from "../../../core/groups/GroupPicker.tsx"
import { chipGroup, groupName } from "../../../core/groups/group.ts"
import { formatList } from "../../../core/i18n/format.ts"
import { t as coreT } from "../../../core/i18n/index.ts"
import { PersonChip } from "../../../core/people/PersonChip.tsx"
import { isAdmin, isTeamMember, type StandingSource } from "../../../core/people/standing.ts"
import type { GroupId } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Button, Dialog, FormAlert } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import type { ConversationView } from "../wire/views.ts"
import { changeOwners } from "./manage.ts"
import { ownerCandidates } from "./owners.ts"

/**
 * Whether this reader may change a channel's groups: not archived, and an administrator, or a member
 * of the channel who is a team member — the cells of `chat.owner.add` and `chat.owner.remove`.
 */
export function canManageOwners(
	conv: Pick<ConversationView, "kind" | "archivedAt">,
	isMember: boolean,
	me: StandingSource | null | undefined,
): boolean {
	if (conv.kind !== "channel" || conv.archivedAt !== undefined) return false
	return isAdmin(me) || (isMember && isTeamMember(me))
}

export function ChannelGroups({ conv, isMember }: { conv: ConversationView; isMember: boolean }) {
	const groups = useModel((model) => model.groups)
	const canManage = useModel((model) => canManageOwners(conv, isMember, model.me))
	const meId = useModel((model) => model.me?.id)
	const [choosing, setChoosing] = useState(false)
	// The chosen groups an administrator outside the channel is asked about before they are applied.
	const [opening, setOpening] = useState<GroupId[] | null>(null)
	const candidates = useMemo(() => ownerCandidates(groups), [groups])
	const owners = conv.ownerGroupIds
	// Only the groups the reader's model knows are drawn; the server sends live owners only.
	const known = owners.flatMap((id) => {
		const group = groups[id]
		return group === undefined ? [] : [group]
	})

	const change = useAsyncAction(async (next: GroupId[]) => {
		await changeOwners(conv.id, owners, next)
	})
	// The added groups the reader is in, when the reader is outside the channel: adding one opens
	// the channel's messages to them.
	const addedHoldingMe = (next: GroupId[]) =>
		isMember || meId === undefined
			? []
			: next.flatMap((id) => {
					const group = groups[id]
					return !owners.includes(id) && group?.memberIds.includes(meId) === true ? [group] : []
				})

	return (
		<section>
			<h3>{t("chat.details.groups.title")}</h3>
			<p className="tb-convdetails__fact">
				{known.length === 0 ? t("chat.details.groups.none") : t("chat.details.groups.lead")}
			</p>
			{known.length === 0 ? null : (
				<ul className="tb-convdetails__members">
					{known.map((group) => (
						<li className="tb-convdetails__row" key={group.id}>
							<PersonChip person={chipGroup(group)} hover={false} handle />
						</li>
					))}
				</ul>
			)}
			{canManage ? (
				<Button
					variant="secondary"
					size="sm"
					label={t("chat.details.groups.change")}
					onClick={() => {
						change.reset()
						setChoosing(true)
					}}
				/>
			) : null}
			<FormAlert messageKey={change.error} />
			<ChooseGroupsDialog
				open={choosing}
				value={owners}
				candidates={candidates}
				warnWhenNotIn={false}
				onClose={() => setChoosing(false)}
				onApply={(chosen) => {
					setChoosing(false)
					if (addedHoldingMe(chosen).length > 0) setOpening(chosen)
					else void change.run(chosen)
				}}
			/>
			<Dialog
				open={opening !== null}
				onOpenChange={(open) => {
					if (!open) setOpening(null)
				}}
				title={t("chat.details.groups.selfTitle")}
				description={t("chat.details.groups.selfBody", {
					groups: formatList(addedHoldingMe(opening ?? []).map(groupName)),
				})}
				actions={
					<>
						<Button
							variant="neutral"
							label={coreT("action.cancel")}
							onClick={() => setOpening(null)}
						/>
						<Button
							variant="primary"
							label={coreT("groups.choose.apply")}
							onClick={() => {
								const chosen = opening
								setOpening(null)
								if (chosen !== null) void change.run(chosen)
							}}
						/>
					</>
				}
			/>
		</section>
	)
}
